import React from 'react'
import { Label, Segment, Timeline } from '@waylay/react-components'
import { DateTime } from 'luxon'
import { round } from 'lodash-es'

interface INodeHistoryProps {
  timeline: ITimelineEvent[]
}

export enum TimelineEventType {
  ActuatorInvocation,
  ProbabilityChange,
}

export interface ITimelineEvent {
  timestamp: DateTime
  nodeLabel: string
  type: TimelineEventType
  error?: string
}

const EventTitle = ({ timestamp }: { timestamp: DateTime }) => (
  <Timeline.Header>
    {timestamp.toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)}
  </Timeline.Header>
)

interface ISensorInvocation extends ITimelineEvent {
  state: string
  probability: number
}

function toTimelineEvent(event: ISensorInvocation | IActuatorInvocation) {
  if (isSensorInvocationEvent(event)) {
    return (
      <>
        <EventTitle timestamp={event.timestamp} />
        <Timeline.EntryGroup>
          <Timeline.Entry>
            <Label focused name={event.state}>
              {round((event.probability / 1) * 100, 2)}% probability
            </Label>
          </Timeline.Entry>
        </Timeline.EntryGroup>
      </>
    )
  }

  if (isActuatorInvocationEvent(event)) {
    return (
      <>
        <EventTitle timestamp={event.timestamp} />
        <Timeline.EntryGroup>
          <Timeline.Entry>
            {event.error ? (
              <code>{event.error}</code>
            ) : (
              <Label focused name={event.node}>
                triggered this node
              </Label>
            )}
          </Timeline.Entry>
        </Timeline.EntryGroup>
      </>
    )
  }
}

interface IActuatorInvocation extends ITimelineEvent {
  error?: string
  node: string
}

function isSensorInvocationEvent(
  event: ISensorInvocation | IActuatorInvocation,
): event is ISensorInvocation {
  return (event as ISensorInvocation).state != null
}

function isActuatorInvocationEvent(
  event: ISensorInvocation | IActuatorInvocation,
): event is IActuatorInvocation {
  return (event as IActuatorInvocation).node !== undefined
}

function NodeHistory(props: INodeHistoryProps & { css?: any }) {
  const { timeline, css, ...rest } = props

  return (
    <Segment.Group css={css} {...rest}>
      <Segment.Header>History</Segment.Header>
      <Segment>
        {timeline.length > 0 ? (
          <Timeline>{timeline.map(toTimelineEvent)}</Timeline>
        ) : (
          <span>No history</span>
        )}
      </Segment>
    </Segment.Group>
  )
}

export default NodeHistory
